'use client';

import cn from 'classnames';
import { ProductCard } from 'components/product-card';
import { SimpleImage, TSimpleImageProps } from 'components/simple-image';
import { Spacer } from 'components/ui-kit';
import { Column, Container, Row } from 'components/ui-kit/grid';
import { Visibility } from 'components/ui-kit/visibility';
import { TProductResponse } from 'lib';
import Link from 'next/link';
import BraceleteImage from 'public/images/collaborations/utkonos/bracelete_image.png';
import LongslivImage from 'public/images/collaborations/utkonos/long_image.png';
import StickersImage from 'public/images/collaborations/utkonos/stickers_image.png';
import { type JSX } from 'react';
import styles from '../collaboration-utkonos-page.module.scss';

type TData = {
  content: React.ReactNode;
  image: TSimpleImageProps['src'];
  weight: number;
};

const data: Map<string, TData> = new Map([
  [
    'uniseks-braslet-menyajsya',
    {
      weight: 2,
      image: BraceleteImage,
      content: (
        <>
          Изящное напоминание о&nbsp;том, что стоять на&nbsp;месте&nbsp;&mdash;
          так себе практика. Дорогу осилит идущий, а&nbsp;изменчивый
          мир&nbsp;&mdash; готовый адаптироваться, перепридумывать
          и&nbsp;изобретать что-то новое. Чтобы не&nbsp;забывать об&nbsp;этом,
          просто наденьте наш браслет.
        </>
      ),
    },
  ],
  [
    'utkonosovye-stikery',
    {
      weight: 6,
      image: StickersImage,
      content: (
        <>
          Это не&nbsp;просто милая зверушка с&nbsp;ценным мехом,
          а&nbsp;настоящий символ адаптации и&nbsp;пример того, как важно
          меняться и&nbsp;менять.
        </>
      ),
    },
  ],
  [
    'utkonosovyj-longsliv',
    {
      weight: 7,
      image: LongslivImage,
      content: (
        <>
          &laquo;Чего&nbsp;же ты&nbsp;ждёшь?&raquo;&nbsp;&mdash; вполне разумный
          вопрос от&nbsp;утконоса. А&nbsp;что ещё может спросить такой необычный
          зверь?
        </>
      ),
    },
  ],
]);

const getResult = (
  score: number,
  products: Array<TProductResponse>,
): Array<TProduct> => {
  const productsWithAdditional = products.map<TProduct>(
    (product: TProductResponse) => ({
      ...product,
      additional: data.get(product.slug),
    }),
  );

  let productSlug = 'uniseks-braslet-menyajsya';

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (score > 2) {
    productSlug = 'utkonosovye-stikery';
  }

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (score > 6) {
    productSlug = 'utkonosovyj-longsliv';
  }

  const target = productsWithAdditional.find(
    (product: TProduct) => product.slug === productSlug,
  );

  const others = products.filter(
    (product: TProduct) => product.slug !== productSlug,
  );

  return [target, ...others] as Array<TProduct>;
};

type TProduct = TProductResponse & { additional?: TData };
type TProductsBlockProps = {
  products: Array<TProductResponse>;
  score: number;
};
export const ProductsBlock = ({
  products,
  score,
}: TProductsBlockProps): JSX.Element => {
  const [resultProduct, ...otherProducts] = getResult(score, products);

  console.log(
    score,
    products
      .map<TProduct>((product: TProductResponse) => ({
        ...product,
        additional: data.get(product.slug),
      }))
      .sort(
        (product: TProduct, bProduct: TProduct) =>
          (product.additional?.weight ?? 0) +
          (bProduct.additional?.weight ?? 0) -
          score,
      ),
  );

  return (
    <Container>
      {resultProduct ? (
        <Row className={styles.productCard}>
          <Column order={{ s: 2, m: 2, l: 1 }} span={{ s: 12, m: 12, l: 6 }}>
            <Link
              href={`/product/${resultProduct.slug}`}
              className={styles.productCardImageWrapper}
            >
              <div className="ratio-11-16 ratio-m-1-1 ratio-l-11-16">
                <SimpleImage
                  fill
                  alt={resultProduct.name}
                  src={resultProduct.previewImage?.src}
                />
              </div>
              <Visibility hidden="m">
                <Spacer size={28} />
                <Link
                  className={styles.productCardButton}
                  href={`/product/${resultProduct.slug}`}
                >
                  Забрать себе
                </Link>
              </Visibility>
            </Link>
          </Column>
          <Column
            order={{ s: 1, m: 1, l: 2 }}
            span={{ s: 12, m: 12, l: 6 }}
            className={styles.productCardInner}
          >
            <div className={styles.productCardTitleWrapper}>
              <h3 className={styles.productCardTitle}>{resultProduct.name}</h3>
            </div>
            <div className={cn(styles.bodyText, styles.productCartContent)}>
              {resultProduct.additional?.content ?? resultProduct.description}
            </div>
            <div className={cn(styles.productCardImage)}>
              <SimpleImage
                objectFit="contain"
                alt={resultProduct.name}
                src={resultProduct.additional?.image}
              />
            </div>

            <br />
            <Visibility visible="m">
              <Link
                className={styles.productCardButton}
                href={`/product/${resultProduct.slug}`}
              >
                Забрать себе
              </Link>
            </Visibility>
          </Column>
        </Row>
      ) : null}
      <Row justify={{ s: 'center' }}>
        {otherProducts.map((product: TProduct) => {
          return (
            <Column
              span={{ s: 8, m: 4 }}
              key={product.slug}
              className={styles.productCardPreviewWrapper}
            >
              <Link href={`/product/${product.slug}`}>
                <ProductCard product={product} />
              </Link>
            </Column>
          );
        })}
      </Row>
    </Container>
  );
};
